// src/hooks/useReferencesEDE.ts — SCE Issues
// Le schéma électrique le plus récent connu, partagé par NVS (26/09).
//
// Deux équipements de même NVS (lu dans la fourniture Windchill du
// référentiel) partagent leur EDE : la révision la plus récente se lit donc
// sur les problèmes de TOUS ces équipements, pas seulement du N° de série
// affiché. Le référentiel vient du cache mémoire (`listAllEquipements`) ;
// tant qu'il n'est pas là — ou s'il échoue — on retombe sur le N° de série,
// comme au Lot 13.

import { useEffect, useMemo, useState } from 'react'
import { listAllEquipements } from '@/api/equipements.api'
import type { Equipement, Issue } from '@/types/issue.types'
import {
  cleEDEDepuisReferentiel,
  cleParNumeroSerie,
  referencesParEquipement,
  type CleEDE,
  type ReferenceEDE,
} from '@/utils/documentationEDE'

export interface ReferencesEDE {
  refs: Map<string, ReferenceEDE>
  cle: CleEDE
}

/** @param issues les problèmes où chercher les révisions (en général : tous). */
export function useReferencesEDE(issues: readonly Issue[]): ReferencesEDE {
  const [equipements, setEquipements] = useState<Equipement[] | null>(null)

  useEffect(() => {
    let annule = false
    listAllEquipements()
      .then((liste) => {
        if (!annule) setEquipements(liste)
      })
      .catch((err: unknown) => {
        console.warn('[useReferencesEDE] référentiel indisponible, EDE par N° de série :', err)
      })
    return () => {
      annule = true
    }
  }, [])

  const cle = useMemo(
    () => (equipements ? cleEDEDepuisReferentiel(equipements) : cleParNumeroSerie),
    [equipements],
  )
  const refs = useMemo(() => referencesParEquipement(issues, cle), [issues, cle])
  return { refs, cle }
}
